/**
 * VersionProgressCard Component
 *
 * Shared component for displaying version processing progress.
 * Used by both table creation wizard and version upload page.
 *
 * Features:
 * - Real-time progress tracking with polling
 * - Processing steps with status indicators
 * - Live log streaming from API
 * - Success/error states with action buttons
 */

import { useMemo, useState, useEffect } from 'react';
import {
  AlertCircle,
  Upload,
  Circle,
  CheckCircle2,
  Loader2,
  PartyPopper,
  XCircle,
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Progress,
} from '@loader/ui';

import { VersionLogs } from '@/components/common/version-progress/VersionLogs';
import { ValidationErrors } from '@/components/common/version-progress/ValidationErrors';
import { FoldingCube } from '@/components/common/FoldingCube';
import { useVersionProgress } from '@/hooks/useVersionProgress';
import { useRpc } from '@/lib/rpc';
import type { LogEntry } from '@/features/versions/types';

const VERSION_POLL_INTERVAL_MS = 1000;
const DEFAULT_ERROR_LIMIT = 100;

/**
 * Processing steps - simplified to show meaningful phases only.
 *
 * The backend has finer-grained progress (20% initial, 70% DuckDB complete, 95% finalizing)
 * but those intermediate phases are instant database operations, not visible to users.
 *
 * Flow:
 * 1. Queued - File received, waiting in queue (handled separately)
 * 2. Processing data - All backend processing (parsing, validating, converting, saving)
 * 3. Complete - Done
 */
const PROCESSING_STEPS = [
  { id: 'processing', label: 'Processing data', activeLabel: 'Processing data...', threshold: 99 },
  { id: 'complete', label: 'Complete', activeLabel: 'Complete', threshold: 100 },
] as const;

interface StoredLog {
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'success';
  message: string;
  details?: Record<string, unknown> | null;
}

interface VersionInfo {
  id: string;
  tableId: string;
  fileName: string;
}

interface VersionProgressCardProps {
  versionId: string | undefined;
  onComplete?: (version: VersionInfo) => void;
  onError?: () => void;
  errorLabel?: string;
  showValidationErrors?: boolean;
}

/** Hook for fetching and managing version logs via operation endpoints */
function useVersionLogs(
  version: {
    id: string;
    table_id: string;
    operation_id?: string | null;
    status: string;
    created_at: string;
    completed_at?: string | null;
    records_processed: number;
    records_total: number;
    error_message?: string | null;
  } | null | undefined
): LogEntry[] {
  const rpc = useRpc();
  const [storedLogs, setStoredLogs] = useState<StoredLog[]>([]);

  useEffect(() => {
    if (!version?.operation_id || !version?.table_id) return;

    const tableId = version.table_id;
    const operationId = version.operation_id;
    let isLoadingRef = false;

    const fetchLogsFromAPI = async () => {
      if (isLoadingRef) return;

      const versionStatus = version?.status;
      if (
        version &&
        versionStatus &&
        (versionStatus === 'completed' ||
          versionStatus === 'failed' ||
          versionStatus === 'processing')
      ) {
        isLoadingRef = true;
        try {
          // @ts-expect-error - Hono RPC type inference limitation for nested routes
          const logsRes = await rpc.api.private.tables[':id'].operations[':operationId'].logs.$get({
            param: { id: tableId, operationId },
          });
          let logs: StoredLog[] = [];

          if (logsRes.ok) {
            const logsData = await logsRes.json();
            logs = logsData.logs.map(
              (log: {
                timestamp: string;
                level: 'info' | 'warn' | 'error' | 'success';
                message: string;
                details?: Record<string, unknown> | null;
              }) => ({
                timestamp: log.timestamp,
                level: log.level,
                message: log.message,
                details: log.details,
              })
            );
          }

          if (versionStatus === 'failed') {
            try {
              // @ts-expect-error - Hono RPC type inference limitation for nested routes
              const errorsRes = await rpc.api.private.tables[':id'].operations[':operationId'].errors.$get({
                param: { id: tableId, operationId },
                query: { page: 1, limit: DEFAULT_ERROR_LIMIT },
              });
              if (errorsRes.ok) {
                const errorsData = await errorsRes.json();
                const validationErrorLogs = errorsData.errors.map(
                  (error: {
                    created_at: string;
                    row_number: number | null;
                    field_name: string | null;
                    message: string;
                    error_code: string;
                    actual_value: unknown;
                    expected_value: string | null;
                  }) => ({
                    timestamp: error.created_at,
                    level: 'error' as const,
                    message: `${error.row_number ? `Line ${error.row_number}` : 'Error'}${error.field_name ? ` - Column "${error.field_name}"` : ''}: ${error.message}`,
                    details: {
                      row_number: error.row_number,
                      field_name: error.field_name,
                      error_code: error.error_code,
                      actual_value: error.actual_value,
                      expected_value: error.expected_value,
                    },
                  })
                );
                logs = [...logs, ...validationErrorLogs];
              }
            } catch {
              // Validation errors fetch failed - continue without them
            }
          }

          setStoredLogs(logs);
        } catch {
          // Logs fetch failed - continue without historical logs
        } finally {
          isLoadingRef = false;
        }
      }
    };

    void fetchLogsFromAPI();

    let logsInterval: NodeJS.Timeout | undefined;
    if (version?.status === 'processing') {
      logsInterval = setInterval(() => {
        void fetchLogsFromAPI();
      }, VERSION_POLL_INTERVAL_MS);
    }

    return () => {
      if (logsInterval) clearInterval(logsInterval);
    };
  }, [version?.id, version?.table_id, version?.operation_id, version?.status, version, rpc]);

  return useMemo((): LogEntry[] => {
    if (storedLogs.length > 0) {
      return storedLogs.map((log) => ({
        timestamp: new Date(log.timestamp),
        type: (log.level === 'warn' ? 'warning' : log.level) as
          | 'info'
          | 'warning'
          | 'error'
          | 'success',
        message: log.message,
        details: log.details ?? undefined,
      }));
    }

    if (!version) {
      return [{ timestamp: new Date(), type: 'info', message: 'Waiting for version...' }];
    }

    const baseLogs: LogEntry[] = [
      { timestamp: new Date(version.created_at), type: 'info', message: 'Version started' },
    ];

    if (version.status === 'processing') {
      baseLogs.push({
        timestamp: new Date(),
        type: 'info',
        message: `Processing records: ${version.records_processed}/${version.records_total}`,
      });
    } else if (version.status === 'completed') {
      baseLogs.push({
        timestamp: new Date(version.completed_at ?? new Date()),
        type: 'success',
        message: `Version completed successfully. Processed ${version.records_processed} records.`,
      });
    } else if (version.status === 'failed') {
      baseLogs.push({
        timestamp: new Date(),
        type: 'error',
        message: version.error_message ?? 'Version failed with unknown error',
      });
    }

    return baseLogs;
  }, [version, storedLogs]);
}

/** Processing steps indicator */
function ProcessingSteps({
  progress,
  status,
  recordsProcessed,
  recordsTotal,
}: {
  progress: number;
  status: string;
  recordsProcessed?: number;
  recordsTotal?: number;
}) {
  // Show "Queued" step when status is 'queued' (before processing starts)
  const isQueued = status === 'queued';

  const getStepStatus = (stepIndex: number) => {
    // If queued, all steps are pending
    if (isQueued) return 'pending';

    if (status === 'failed') {
      const currentStepIndex = PROCESSING_STEPS.findIndex(
        (step) => progress < step.threshold
      );
      const failedStepIndex =
        currentStepIndex === -1 ? PROCESSING_STEPS.length - 1 : currentStepIndex;

      if (stepIndex < failedStepIndex) return 'completed';
      if (stepIndex === failedStepIndex) return 'error';
      return 'pending';
    }

    if (status === 'completed') return 'completed';

    const currentStepIndex = PROCESSING_STEPS.findIndex(
      (step) => progress < step.threshold
    );

    if (currentStepIndex === -1) return 'completed';
    if (stepIndex < currentStepIndex) return 'completed';
    if (stepIndex === currentStepIndex) return 'active';
    return 'pending';
  };

  // Get the active step for record count display
  const activeStepIndex = PROCESSING_STEPS.findIndex(
    (step) => progress < step.threshold
  );
  const showRecordCount =
    recordsTotal &&
    recordsTotal > 0 &&
    activeStepIndex === 0; // Processing step

  return (
    <div className="space-y-3">
      {/* Queued step - only shown when status is 'queued' */}
      {isQueued && (
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
          <span className="font-light">Waiting in queue...</span>
        </div>
      )}

      {PROCESSING_STEPS.map((step, index) => {
        const stepStatus = getStepStatus(index);
        const isActive = stepStatus === 'active';

        // Show record count for active processing step
        const stepLabel =
          isActive && showRecordCount && recordsProcessed !== undefined
            ? `${step.activeLabel} ${recordsProcessed.toLocaleString()} / ${recordsTotal?.toLocaleString()} rows`
            : isActive
              ? step.activeLabel
              : step.label;

        return (
          <div key={step.id} className="flex items-center gap-3">
            {stepStatus === 'completed' && (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            )}
            {stepStatus === 'active' && (
              <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
            )}
            {stepStatus === 'pending' && (
              <Circle className="h-5 w-5 text-muted-foreground/40" />
            )}
            {stepStatus === 'error' && <XCircle className="h-5 w-5 text-destructive" />}
            <span className="font-light">{stepLabel}</span>
          </div>
        );
      })}
    </div>
  );
}

/** Main progress view with states: processing, success, error */
function ProgressView({
  progress,
  status,
  recordsProcessed,
  recordsTotal,
  fileName,
  onNext,
  onTryAgain,
  onSeeLogs,
}: {
  progress: number;
  status: string;
  recordsProcessed?: number;
  recordsTotal?: number;
  fileName?: string;
  onNext?: () => void;
  onTryAgain?: () => void;
  onSeeLogs?: () => void;
}) {
  if (status === 'completed') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-6">
        <div className="rounded-2xl bg-green-100 p-6 dark:bg-green-950/30">
          <PartyPopper className="h-16 w-16 text-green-600 dark:text-green-400" />
        </div>
        <span className="text-2xl font-semibold">Congrats, your file is ready!</span>
        <div className="flex gap-3">
          <Button className="w-32" onClick={onNext}>
            Next
          </Button>
          <Button variant="outline" className="w-32" onClick={onSeeLogs}>
            See logs
          </Button>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-6">
        <div className="rounded-2xl bg-red-100 p-6 dark:bg-red-950/30">
          <XCircle className="h-16 w-16 text-red-600 dark:text-red-400" />
        </div>
        <span className="text-2xl font-semibold">Oops, something went wrong</span>
        <div className="flex gap-3">
          <Button className="w-32" onClick={onTryAgain}>
            Try again
          </Button>
          <Button variant="outline" className="w-32" onClick={onSeeLogs}>
            See logs
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6">
      <FoldingCube size="lg" />
      <div className="text-9xl font-bold">{progress}%</div>
      {fileName && (
        <div className="text-muted-foreground">Processing {fileName}</div>
      )}
      <div className="w-full max-w-md">
        <Progress value={progress} className="h-1" />
      </div>
      <ProcessingSteps
        progress={progress}
        status={status}
        recordsProcessed={recordsProcessed}
        recordsTotal={recordsTotal}
      />
    </div>
  );
}

export function VersionProgressCard({
  versionId,
  onComplete,
  onError,
  errorLabel = 'Go Back',
  showValidationErrors = true,
}: VersionProgressCardProps) {
  const [activeView, setActiveView] = useState<'progress' | 'logs'>('progress');

  const {
    version,
    isLoading: isLoadingVersion,
    error: versionError,
  } = useVersionProgress(versionId);

  const logs = useVersionLogs(version);

  if (isLoadingVersion && !version) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-4" role="status" aria-live="polite">
        <div className="flex items-center justify-center py-12">
          <FoldingCube text="Loading version information..." size="md" />
        </div>
      </div>
    );
  }

  if (versionError) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-4" role="alert">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Unable to load version information. Please try again or contact support.
          </AlertDescription>
        </Alert>
        {onError && (
          <Button variant="outline" onClick={onError}>
            {errorLabel}
          </Button>
        )}
      </div>
    );
  }

  const isProcessing = version?.status === 'queued' || version?.status === 'pending' || version?.status === 'processing';
  const isCompleted = version?.status === 'completed';
  const isFailed = version?.status === 'failed';
  const progress = version?.progress ?? 0;

  return (
    <Card className="flex min-h-0 flex-1 flex-col" role="region" aria-label="Version progress">
      <CardHeader className="border-b">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" aria-hidden="true" />
              Progress
            </CardTitle>
            <CardDescription>
              {isProcessing
                ? 'Processing your file...'
                : isCompleted
                  ? 'Version completed successfully'
                  : isFailed
                    ? 'Version processing failed'
                    : 'Track version progress'}
            </CardDescription>
          </div>
          {activeView === 'logs' && (
            <Button variant="outline" onClick={() => setActiveView('progress')}>
              Back
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex min-h-0 flex-1 flex-col">
        {activeView === 'progress' ? (
          <ProgressView
            progress={progress}
            status={version?.status ?? 'queued'}
            recordsProcessed={version?.records_processed}
            recordsTotal={version?.records_total}
            fileName={version?.file_name}
            onNext={() => {
              if (onComplete && version) {
                onComplete({
                  id: version.id,
                  tableId: version.table_id,
                  fileName: version.file_name,
                });
              }
            }}
            onTryAgain={onError}
            onSeeLogs={() => setActiveView('logs')}
          />
        ) : (
          <>
            <div className="min-h-0 flex-1" role="log" aria-label="Version progress logs">
              <VersionLogs logs={logs} />
            </div>
            {isFailed && showValidationErrors && version?.operation_id && version?.table_id && (
              <div className="mt-4 shrink-0" role="alert" aria-label="Version errors">
                <ValidationErrors
                  operationContext={{
                    tableId: version.table_id,
                    operationId: version.operation_id,
                  }}
                />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
